/* @flow */
import * as React from 'react';
import { computed, observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import type { TranslatorProps } from 'react-i18next';
import { translate } from 'react-i18next';

import ProjectDetailsStore from 'stores/ProjectDetailsStore';
import Loader from '../../components/Loading/Loading';
import AutoCompleteModel from 'models/forms/AutoCompleteModel.js';

import ElectroTextField from '../components/inputs/ElectroTextField/ElectroTextField';
import ElectroSelect from '../components/inputs/ElectroSelect/ElectroSelect';
import ElectroSelectExtra from '../components/inputs/ElectroSelectExtra/ElectroSelectExtra';
import ElectroResponseMessage from '../components/inputs/ElectroResponseMessage';
import ActionDrawerWrapper from 'components/ActionDrawerWrapper/ActionDrawerWrapper';
import PageWrapper from '../../components/PageWrapper/PageWrapper';
import FormWrapper from '../../components/FormWrapper/FormWrapper';
import FormSection from '../../components/FormSection/FormSection';
import FormControls from '../components/controls/FormControls/FormControls';

import { AddNewProject } from 'views/ActionDrawer';

import { autoCompleteTradingAccounts, autoCompleteAccountProject } from 'api/forms.js';

import { createNewProject } from 'permissions/project';

import { makeFields, costCenterCreate, costCenterEdit } from './setupCostCenterForm';

const actionsListData = [
  {
    label: 'general:actions.new_project',
    icon: '#icon-add',
    action: 'newProject',
    permissions: createNewProject,
  },
];

type TypeProps = {|
  mode: string,
  costCenterData: Object,
  projectDetailsStore: ProjectDetailsStore,
|} & TranslatorProps;

@translate()
@inject('projectDetailsStore', 'actionDrawerStore')
@observer
export default class CostCenterForm extends React.Component<*> {
  /*eslint-disable max-len*/

  @observable accountsModel = new AutoCompleteModel(autoCompleteTradingAccounts);

  @observable costCenterProject = new AutoCompleteModel(autoCompleteAccountProject);

  @observable localState = {
    selectedValuePrjc: '',
    responseMessage: '',
    showResponseMessage: false,
    isLoading: false,
    hasSuccess: false,
    hasError: false,
  };

  props: TypeProps;

  getMode = () => this.props.mode;

  componentDidMount() {
    const costCenterData: Object =
      typeof this.props.costCenterData === 'undefined' ? {} : this.props.costCenterData;

    let projectsStr: string = '';

    if (costCenterData && costCenterData.projects && costCenterData.projects.length > 0) {
      for (let i: * = 0; i < costCenterData.projects.length; i++) {
        projectsStr !== '' && (projectsStr += ',');
        projectsStr += costCenterData.projects[i].id;
      }
    }

    this.localState.selectedValuePrjc = projectsStr;
  }

  @computed get form(): Object {
    const costCenterData: Object =
      typeof this.props.costCenterData === 'undefined' ? {} : this.props.costCenterData;
    const fields: Array<*> = makeFields(costCenterData, this.props.t);
    // const fields: Array<*> = makeFields(this.props.projectDetailsStore.details);

    return this.getMode() === 'edit'
      ? costCenterEdit(fields, costCenterData, this.localState, this.props.t)
      : costCenterCreate(fields, this.localState, this.props.t);
  }

  handleOnChangeProject = (selectedValue: string) => {
    this.localState.selectedValuePrjc = selectedValue;
  };

  resetLocations() {
    const resetModels = [];

    const resetFields = [
      // Extra Fields
      this.form.$('assignProjects.projectAssign'),
    ];

    // Reset Models
    for (let i: * = 0; i < resetModels.length; i++) {
      resetModels[i].clear();
      resetModels[i].loadData();
    }

    // Reset Fields
    for (let i: * = 0; i < resetFields.length; i++) {
      resetFields[i].value = '';
      resetFields[i].extra.selectID = '';
    }

    // Reset values on projects
    this.localState.selectedValuePrjc = '';
  }

  render(): React.Element<*> {
    const { t, projectDetailsStore } = this.props;
    const form = this.form;

    const accountsModel = this.accountsModel;
    const costCenterProject = this.costCenterProject;

    return projectDetailsStore.loadingProjectDetails || this.localState.formIsLoading ? (
      <Loader />
    ) : (
      <React.Fragment>
        <PageWrapper isFlex={false}>
          <ElectroResponseMessage localState={this.localState} />
          <FormWrapper form={form}>
            {/* ================| CHOOSE ACCOUNT |================ */}
            <FormSection form={form} section="chooseAccount">
              <ElectroSelectExtra
                field={form.$('chooseAccount.tradingAccount')}
                form={form}
                formName="chooseAccount"
                labelKey="name"
                // updateCustomFields={() => this.updateCustomFields()}
                resetModels={() => this.resetLocations()}
                props={{
                  model: accountsModel,
                  modelChild: costCenterProject,
                }}
              />
            </FormSection>
            {/* ================| COST CENTER DETAIL |================ */}
            <FormSection form={form} section="costCenterDetails">
              <ElectroTextField field={form.$('costCenterDetails.costCenterCode')} />
              <ElectroTextField field={form.$('costCenterDetails.costCenterName')} />
              <ElectroTextField
                field={form.$('costCenterDetails.costCenterDescription')}
                props={{
                  multiline: true,
                  rows: 5,
                }}
              />
              <ElectroSelect
                field={form.$('costCenterDetails.chooseStatus')}
                searchable={false}
                simpleValue={true}
              />
            </FormSection>
            {/* ================| ASSIGN PROJECTS |================ */}
            <FormSection form={form} section="assignProjects">
              <ElectroSelectExtra
                field={form.$('assignProjects.projectAssign')}
                form={form}
                formName="assignProjects"
                labelKey={['code', 'name']}
                actionButtonData={{
                  data: actionsListData[0],
                  disabled: !form.$('chooseAccount.tradingAccount').extra.selectID,
                }}
                multi={true}
                handleOnChange={this.handleOnChangeProject}
                singleSelectedValue={this.localState.selectedValuePrjc}
                props={{
                  model: costCenterProject,
                }}
              />
            </FormSection>
            {/* ================| ADDITIONAL INFORMATION |================ */}
            <FormSection form={form} section="additionalInformation">
              <ElectroTextField
                field={form.$('additionalInformation.comments')}
                props={{
                  multiline: true,
                  rows: 7,
                }}
              />
            </FormSection>
            <FormControls
              buttonText={
                this.props.mode === 'edit'
                  ? t('general:actions.update')
                  : t('general:actions.create')
              }
              form={form}
            />
          </FormWrapper>
        </PageWrapper>
        <ActionDrawerWrapper
          onActionCompleted={() => {
            /*overriding with empty fn*/
          }}
        >
          {
            {
              newProject: (
                <AddNewProject
                  parentForm={form}
                  accountsModel={accountsModel}
                  model={costCenterProject}
                  localState={this.localState}
                />
              ),
            }[this.props.actionDrawerStore.currentActionDrawer]
          }
        </ActionDrawerWrapper>
      </React.Fragment>
    );
  }
}
